/** Query param and session keys for partner email invite flow */

export const INVITE_TOKEN_QUERY = 'inviteToken';

export const INVITE_TOKEN_STORAGE_KEY = 'gradmap_partner_invite_token';

export const INVITE_META_STORAGE_KEY = 'gradmap_partner_invite_meta';

/** Set after POST /partner/invite/accept when user must finish phone onboarding before Settings */
export const INVITE_REDIRECT_AFTER_ONBOARDING_KEY = 'gradmap_partner_invite_redirect_settings';

export type PartnerInviteKind = 'studentInviteParent' | 'parentInviteStudent';

export interface PartnerInviteValidateResponse {
  valid: boolean;
  statusCode?: number;
  invite_kind?: PartnerInviteKind;
  invitee_email?: string;
  invitee_first_name?: string;
  invitee_last_name?: string;
  expires_at?: string;
  message?: string;
}

export function getInviteTokenFromSearch(search: string): string | null {
  if (!search) return null;
  const q = search.startsWith('?') ? search.slice(1) : search;
  const t = new URLSearchParams(q).get(INVITE_TOKEN_QUERY);
  return t && t.trim() ? t.trim() : null;
}

/** Remove inviteToken from a query string (e.g. after create-student consumed the invite). */
export function stripInviteTokenFromSearch(search: string): string {
  if (!search || search.length <= 1) return '';
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const p = new URLSearchParams(raw);
  p.delete(INVITE_TOKEN_QUERY);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * HashRouter only reads the query string inside the hash (e.g. #/public/register?inviteToken=…).
 * If someone opens http://host/public/register?inviteToken=… (query on the document URL), React Router
 * does not see inviteToken. This also avoids bugs from setting location.hash to "?…" when hash was empty.
 */
export function syncInviteTokenDocumentSearchToHashRouter(): void {
  if (typeof window === 'undefined') return;
  const w = window.location;
  if (!w.search || w.search.length <= 1) return;
  const doc = new URLSearchParams(w.search.slice(1));
  if (!doc.get(INVITE_TOKEN_QUERY)) return;

  const hash = w.hash || '';
  const hashQuery = hash.includes('?') ? hash.split('?').slice(1).join('?') : '';
  if (hashQuery && new URLSearchParams(hashQuery).get(INVITE_TOKEN_QUERY)) {
    return;
  }

  const pathname = w.pathname && w.pathname !== '/' ? w.pathname : '/public/register';
  const newUrl = `${w.origin}/#${pathname}${w.search}`;
  w.replace(newUrl);
}

/** Use router search when present; otherwise document search (before sync) so inviteToken is found. */
export function getEffectiveSearch(routerSearch: string): string {
  if (routerSearch && routerSearch.length > 1) return routerSearch;
  if (typeof window !== 'undefined' && window.location.search && window.location.search.length > 1) {
    return window.location.search;
  }
  return '';
}

/** Merge/update query string while preserving inviteToken when present in storage or URL */
export function mergeSearchPreserveInvite(
  currentSearch: string,
  updates: Record<string, string | undefined | null>
): string {
  const raw = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
  const p = new URLSearchParams(raw);
  Object.entries(updates).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') p.delete(k);
    else p.set(k, v);
  });
  const stored = sessionStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
  if (stored && !p.get(INVITE_TOKEN_QUERY)) {
    p.set(INVITE_TOKEN_QUERY, stored);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function persistInviteToken(token: string | null): void {
  if (token) sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
  else sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
}

export function getPersistedInviteToken(): string | null {
  return sessionStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
}

export function persistInviteMeta(meta: Partial<PartnerInviteValidateResponse> | null): void {
  if (meta) sessionStorage.setItem(INVITE_META_STORAGE_KEY, JSON.stringify(meta));
  else sessionStorage.removeItem(INVITE_META_STORAGE_KEY);
}

export function getInviteMeta(): Partial<PartnerInviteValidateResponse> | null {
  const raw = sessionStorage.getItem(INVITE_META_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<PartnerInviteValidateResponse>;
  } catch {
    return null;
  }
}

export function clearPartnerInviteContext(): void {
  sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(INVITE_META_STORAGE_KEY);
}

export function setInviteRedirectAfterOnboarding(): void {
  sessionStorage.setItem(INVITE_REDIRECT_AFTER_ONBOARDING_KEY, '1');
}

export function consumeInviteRedirectAfterOnboarding(): boolean {
  const v = sessionStorage.getItem(INVITE_REDIRECT_AFTER_ONBOARDING_KEY);
  if (v) {
    sessionStorage.removeItem(INVITE_REDIRECT_AFTER_ONBOARDING_KEY);
    return true;
  }
  return false;
}

export function inviteKindLabel(kind?: PartnerInviteKind): string {
  if (kind === 'studentInviteParent') return 'parent';
  if (kind === 'parentInviteStudent') return 'student';
  return 'invitee';
}
